/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ForgePioGithubIcon } from '@atlassian/rovo-connectors-config/icons';

import { onBannerDismissed } from '../../../../../common/utils/events/banner-dismissed';
import { useLogException } from '../../../../../controllers/error-boundary/utils';
import { useQuickFindAnalytics } from '../../../../../controllers/quick-find/analytics';
import { usePrimaryProduct, useUser } from '../../../../../controllers/store/bootstrap';

import { detectJiraGninIntent } from './jira-gnin-intent';
import { detectGithubTipKeyword } from './keywords';
import { messages } from './messages';
import { dismissGithubTipForKeyword, shouldShowGithubTipForKeyword } from './storage';

const styles = cssMap({
	// Outer wrapper that insets the banner from the dialog edges and provides
	// vertical spacing. Lives here (not in the dialog) so the spacing is tied to
	// the banner's visibility — when the banner self-hides, no empty box or
	// trailing divider is left behind.
	outer: {
		marginInlineStart: token('space.100'),
		marginInlineEnd: token('space.100'),
		marginBlockStart: token('space.100'),
		marginBlockEnd: token('space.100'),
	},
	// Variant used in the intent-router layout: the preceding intent rows already
	// add `space.100` of bottom padding, so pull the banner up to keep the gap
	// consistent with the spacing between intent rows.
	outerNegativeTop: {
		marginInlineStart: token('space.100'),
		marginInlineEnd: token('space.100'),
		marginBlockStart: token('space.negative.100'),
		marginBlockEnd: token('space.100'),
	},
	banner: {
		display: 'flex',
		width: '100%',
		alignItems: 'center',
		boxSizing: 'border-box',
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		borderRadius: token('radius.large'),
		color: token('color.text'),
	},
	iconWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		// 24px matches the result/recents rows' icon area (`size.200`) and the
		// 24x24 GitHub SVG. `width`/`height` in cssMap take literal dimensions,
		// not space tokens.
		width: '24px',
		height: '24px',
	},
	text: {
		flex: '1 1 auto',
		marginLeft: token('space.150'),
		marginRight: token('space.150'),
	},
	// Matches the shared dialog `Divider` (a full-width hairline). Inlined here
	// rather than imported to respect the package's no-parent-imports boundary.
	divider: {
		height: '0',
		width: '100%',
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderColor: token('color.border'),
	},
});

const linkOverrideStyles = css({
	appearance: 'none',
	padding: 0,
	margin: 0,
	border: 'none',
	background: 'none',
	font: 'inherit',
	cursor: 'pointer',
	textDecoration: 'none',
	color: token('color.link'),
	borderRadius: token('radius.small'),
	'&:hover': {
		textDecoration: 'none',
		color: token('color.link'),
	},
	'&:focus': {
		textDecoration: 'none',
		color: token('color.link'),
	},
	'&:focus-visible': {
		outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
		outlineOffset: token('space.025'),
	},
	'&:active': {
		textDecoration: 'none',
		color: token('color.link.pressed'),
	},
});

export type GithubTipBannerProps = {
	query: string;
	onConnect?: () => void;
	/** When `true`, only literal GitHub keywords trigger the banner (Test 2 cohort). */
	disableGninTrigger?: boolean;
	/**
	 * Render a `Divider` directly after the banner. Used by the standard
	 * (non-intent-router) layout where the divider only makes sense when the
	 * banner is actually shown. The intent-router layout renders its own
	 * always-present divider instead.
	 */
	withTrailingDivider?: boolean;
	/**
	 * Pull the banner up by `space.100` to keep the gap with the preceding
	 * intent-detection rows consistent. Only used in the intent-router layout.
	 */
	useNegativeMarginTop?: boolean;
};

export const GithubTipBanner = ({
	query,
	onConnect,
	disableGninTrigger = false,
	withTrailingDivider = false,
	useNegativeMarginTop = false,
}: GithubTipBannerProps): React.JSX.Element | null => {
	const [user] = useUser();
	const userId = user?.id ?? undefined;
	const [primaryProduct] = usePrimaryProduct();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const logException = useLogException();

	// Triggers (in precedence order): literal GitHub keyword, then Jira
	// GNIN intent (typed JQL / work-item key). Both paths feed the same
	// `matchedKeyword` into storage.
	const { githubHit, gninHit } = useMemo(
		() => ({
			githubHit: detectGithubTipKeyword(query),
			gninHit: disableGninTrigger ? null : detectJiraGninIntent(query, primaryProduct),
		}),
		[query, primaryProduct, disableGninTrigger],
	);
	const matchedKeyword = githubHit ?? gninHit;

	const [sessionDismissedKeyword, setSessionDismissedKeyword] = useState<string | null>(null);

	const [isAllowedByStorage, setIsAllowedByStorage] = useState(false);

	useEffect(() => {
		if (matchedKeyword === null) {
			setIsAllowedByStorage(false);
			return;
		}
		setIsAllowedByStorage(shouldShowGithubTipForKeyword(matchedKeyword, userId));
	}, [matchedKeyword, userId]);

	const isVisible =
		matchedKeyword !== null && sessionDismissedKeyword !== matchedKeyword && isAllowedByStorage;

	const handleConnect = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();
			onConnect?.();
		},
		[onConnect],
	);

	const handleDismiss = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();
			if (matchedKeyword) {
				dismissGithubTipForKeyword(matchedKeyword, userId, logException);
				// When a query matches both triggers, suppress both so the
				// banner stays dismissed regardless of which path activates next.
				if (githubHit && gninHit) {
					dismissGithubTipForKeyword(gninHit, userId, logException);
				}
				setSessionDismissedKeyword(matchedKeyword);
			}
			fireAnalyticsEvent(
				onBannerDismissed({
					isOnSearchDialog: true,
					componentName: 'GithubTipBanner',
				}),
			);
		},
		[matchedKeyword, userId, fireAnalyticsEvent, logException, githubHit, gninHit],
	);

	if (!isVisible) {
		return null;
	}

	return (
		<>
			<Box xcss={useNegativeMarginTop ? styles.outerNegativeTop : styles.outer}>
				<Box xcss={styles.banner} testId="github-tip-banner">
					<Box xcss={styles.iconWrapper} testId="github-tip-banner-icon">
						<ForgePioGithubIcon />
					</Box>
					<Box xcss={styles.text}>
						<Text>
							<FormattedMessage
								{...messages.tipBanner}
								values={{
									b: (chunks) => <Text weight="bold">{chunks}</Text>,
									connectLink: (chunks) => (
										<button
											type="button"
											css={linkOverrideStyles}
											onClick={handleConnect}
											data-testid="github-tip-banner-connect-link"
										>
											{chunks}
										</button>
									),
								}}
							/>
						</Text>
					</Box>
					<IconButton
						icon={CrossIcon}
						spacing="compact"
						onClick={handleDismiss}
						label={<FormattedMessage {...messages.dismissBannerLabel} />}
						appearance="subtle"
						testId="github-tip-banner-dismiss"
					/>
				</Box>
			</Box>
			{withTrailingDivider && <Box xcss={styles.divider} />}
		</>
	);
};
